import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  TrendingUp, Trophy, Target, ChevronDown, BarChart3, Calendar,
  Users, Award, RefreshCw, ArrowRight, AlertTriangle
} from 'lucide-react';
import { QBRankingsTab } from '../components/qb/QBRankingsTab';
import { QBAwardsTab } from '../components/qb/QBAwardsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tab definitions
const TABS = [
  { id: 'rankings', label: 'Rankings', icon: TrendingUp },
  { id: 'weekly', label: 'Weekly Breakdown', icon: Calendar },
  { id: 'season', label: 'Season Totals', icon: BarChart3 },
  { id: 'headtohead', label: 'Head-to-Head', icon: Users },
  { id: 'awards', label: 'Awards', icon: Award },
];

export default function QBAnalyticsPage() {
  const [config, setConfig] = useState(null);
  const [roster, setRoster] = useState([]);
  const [seasonTotals, setSeasonTotals] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [activeTab, setActiveTab] = useState('rankings');
  const [compareQB1, setCompareQB1] = useState('');
  const [compareQB2, setCompareQB2] = useState('');

  // Fetch initial config and data
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch config first to get current week
      const configRes = await axios.get(`${API}/qb/config`);
      const currentConfig = configRes.data;
      setConfig(currentConfig);
      
      // Set selected week to current week (DEFAULT TO CURRENT WEEK)
      const currentWeek = currentConfig.current_week || 6;
      setSelectedWeek(currentWeek);
      
      // Fetch roster and season totals in parallel
      const [rosterRes, totalsRes] = await Promise.all([
        axios.get(`${API}/qb/roster`),
        axios.get(`${API}/qb/season-totals`)
      ]);
      
      setRoster(rosterRes.data);
      setSeasonTotals(totalsRes.data);
      
      // Fetch weekly stats for current week
      const weeklyRes = await axios.get(`${API}/qb/weekly/${currentWeek}`);
      setWeeklyStats(weeklyRes.data);
      
    } catch (error) {
      console.error('Error fetching QB data:', error);
      toast.error('Failed to load QB analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch weekly stats when week changes
  const handleWeekChange = async (week) => {
    setSelectedWeek(week);
    try {
      const res = await axios.get(`${API}/qb/weekly/${week}`);
      setWeeklyStats(res.data);
    } catch (error) {
      console.error('Error fetching weekly stats:', error);
      toast.error('Failed to load weekly stats');
    }
  };

  // Generate week options (1 to current week)
  const weekOptions = config ? Array.from({ length: config.current_week }, (_, i) => i + 1) : [];

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not synced';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading QB Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>2026 SWAC QB Analytics | SheLovesThee</title>
        <meta name="description" content="In-depth quarterback analytics for the 2026 SWAC season. Weekly breakdowns, season totals, and head-to-head comparisons." />
      </Helmet>

      <div className="min-h-screen bg-slate-100" data-testid="qb-analytics-page">
        {/* Header */}
        <div className="bg-[#001f3f] text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase" data-testid="analytics-title">
                  2026 SWAC QB Analytics
                </h1>
                <p className="text-slate-300 mt-1">
                  Season: {config?.current_season} • Current Week: {config?.current_week} • 
                  Last Updated: {formatDate(config?.last_sync)}
                </p>
              </div>
              
              {/* Week Selector - Defaults to current week */}
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-400" />
                <select
                  value={selectedWeek || ''}
                  onChange={(e) => handleWeekChange(parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="week-selector"
                >
                  {weekOptions.map(week => (
                    <option key={week} value={week} className="text-black">
                      Week {week} {week === config?.current_week ? '(Current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-1 mt-6 border-b border-white/20">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-white border-b-2 border-[#DC2626] -mb-[1px]'
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'rankings' && <QBRankingsTab selectedWeek={selectedWeek} />}

          {activeTab === 'awards' && <QBAwardsTab selectedWeek={selectedWeek} roster={roster} />}

          {activeTab === 'weekly' && (
            <WeeklyBreakdownTab 
              weeklyStats={weeklyStats} 
              selectedWeek={selectedWeek}
              currentWeek={config?.current_week}
            />
          )}
          
          {activeTab === 'season' && (
            <SeasonTotalsTab seasonTotals={seasonTotals} />
          )}
          
          {activeTab === 'headtohead' && (
            <HeadToHeadTab 
              roster={roster}
              seasonTotals={seasonTotals}
              compareQB1={compareQB1}
              compareQB2={compareQB2}
              setCompareQB1={setCompareQB1}
              setCompareQB2={setCompareQB2}
            />
          )}
        </div>

        {/* Data Source Attribution */}
        <div className="bg-slate-200 py-4">
          <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-600">
            <p><strong>Statistics Source:</strong> Southwestern Athletic Conference (SWAC)</p>
            <p className="mt-1">
              <strong>Current Week:</strong> Week {config?.current_week} | 
              <strong className="ml-2">Last Updated:</strong> {formatDate(config?.last_sync)}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// ==========================================
// WEEKLY BREAKDOWN TAB
// Shows CALCULATED weekly stats (difference between snapshots)
// ==========================================
function WeeklyBreakdownTab({ weeklyStats, selectedWeek, currentWeek }) {
  if (!weeklyStats) {
    return <div className="text-center py-12 text-slate-500">Loading weekly data...</div>;
  }

  if (weeklyStats.status === 'no_data') {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-slate-700">Weekly Data Not Available</h3>
        <p className="text-slate-500 mt-2">
          {selectedWeek === currentWeek 
            ? 'Weekly data pending - will be available after Sunday sync.'
            : 'No data was recorded for this week.'}
        </p>
        {weeklyStats.results?.length > 0 && (
          <div className="mt-6 max-w-md mx-auto bg-white rounded-xl shadow divide-y text-left" data-testid="weekly-results-only">
            {weeklyStats.results.map(r => (
              <div key={r.player_id} className="px-4 py-2 flex justify-between text-sm">
                <span className="font-bold text-[#001f3f]">{r.name}</span>
                <span><span className={r.result?.startsWith('W') ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>{r.result}</span> {r.opponent} {r.team_record && `(${r.team_record})`}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const stats = weeklyStats.stats || [];

  // Calculate weekly leaders
  const leaders = {
    passYards: stats.reduce((max, qb) => (qb.week_passing_yards || 0) > (max?.week_passing_yards || 0) ? qb : max, null),
    passTD: stats.reduce((max, qb) => (qb.week_passing_tds || 0) > (max?.week_passing_tds || 0) ? qb : max, null),
    compPct: stats.reduce((max, qb) => (qb.week_completion_pct || 0) > (max?.week_completion_pct || 0) ? qb : max, null),
    rushYards: stats.reduce((max, qb) => (qb.week_rushing_yards || 0) > (max?.week_rushing_yards || 0) ? qb : max, null),
    totalTD: stats.reduce((max, qb) => (qb.week_total_tds || 0) > (max?.week_total_tds || 0) ? qb : max, null),
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#001f3f] mb-6 flex items-center gap-2">
        <Calendar className="h-6 w-6" />
        Week {selectedWeek} Breakdown
        {selectedWeek === currentWeek && (
          <span className="text-sm bg-green-500 text-white px-2 py-1 rounded-full ml-2">Current</span>
        )}
      </h2>

      {/* Weekly Leaders Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <LeaderCard title="Top Passing Yards" qb={leaders.passYards} stat={leaders.passYards?.week_passing_yards} unit="yds" />
        <LeaderCard title="Top Passing TDs" qb={leaders.passTD} stat={leaders.passTD?.week_passing_tds} unit="TD" />
        <LeaderCard title="Best Comp %" qb={leaders.compPct} stat={leaders.compPct?.week_completion_pct} unit="%" />
        <LeaderCard title="Top Rushing Yards" qb={leaders.rushYards} stat={leaders.rushYards?.week_rushing_yards} unit="yds" />
        <LeaderCard title="Most Total TDs" qb={leaders.totalTD} stat={leaders.totalTD?.week_total_tds} unit="TD" />
      </div>

      {/* Weekly Stats Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="weekly-stats-table">
            <thead className="bg-[#001f3f] text-white">
              <tr>
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-3 py-3 text-center">COMP</th>
                <th className="px-3 py-3 text-center">ATT</th>
                <th className="px-3 py-3 text-center">COMP %</th>
                <th className="px-3 py-3 text-center">PASS YDS</th>
                <th className="px-3 py-3 text-center">PASS TD</th>
                <th className="px-3 py-3 text-center">INT</th>
                <th className="px-3 py-3 text-center">RUSH YDS</th>
                <th className="px-3 py-3 text-center">RUSH TD</th>
                <th className="px-3 py-3 text-center">TOTAL TD</th>
                <th className="px-3 py-3 text-center">RESULT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stats.map((qb, index) => (
                <tr key={qb.player_id} className="hover:bg-slate-50" data-testid={`weekly-row-${qb.player_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {qb.photo_url ? (
                        <img src={qb.photo_url} alt={qb.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: qb.color || '#333' }}>
                          <span className="text-white font-bold text-sm">{qb.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-[#001f3f]">{qb.name}</div>
                        <div className="text-xs text-slate-500">{qb.school}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">{qb.week_completions || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.week_attempts || 0}</td>
                  <td className="px-3 py-3 text-center font-medium">{qb.week_completion_pct ? `${qb.week_completion_pct}%` : '—'}</td>
                  <td className="px-3 py-3 text-center font-bold text-blue-600">{qb.week_passing_yards || 0}</td>
                  <td className="px-3 py-3 text-center font-bold text-green-600">{qb.week_passing_tds || 0}</td>
                  <td className="px-3 py-3 text-center text-red-500">{qb.week_interceptions || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.week_rushing_yards ?? '—'}</td>
                  <td className="px-3 py-3 text-center">{qb.week_rushing_tds ?? '—'}</td>
                  <td className="px-3 py-3 text-center font-bold bg-[#DC2626]/10 text-[#DC2626]">{qb.week_total_tds || 0}</td>
                  <td className="px-3 py-3 text-center text-sm whitespace-nowrap" data-testid={`weekly-result-${qb.player_id}`}>
                    {qb.result ? <><span className={qb.result.startsWith('W') ? 'text-green-600 font-bold' : 'text-red-500 font-bold'}>{qb.result}</span> <span className="text-slate-500">{qb.opponent}</span>{qb.team_record && <div className="text-xs text-slate-400">{qb.team_record}</div>}</> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-4 text-center">
        Weekly stats are calculated as the difference between Week {selectedWeek} and Week {selectedWeek - 1} cumulative totals.
      </p>
    </div>
  );
}

// ==========================================
// SEASON TOTALS TAB
// Shows CUMULATIVE SWAC stats
// ==========================================
function SeasonTotalsTab({ seasonTotals }) {
  if (!seasonTotals || seasonTotals.length === 0) {
    return <div className="text-center py-12 text-slate-500">No season data available yet.</div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#001f3f] mb-6 flex items-center gap-2">
        <BarChart3 className="h-6 w-6" />
        2026 Season Totals (Cumulative)
      </h2>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="season-totals-table">
            <thead className="bg-[#001f3f] text-white">
              <tr>
                <th className="px-2 py-3 text-center">Rank</th>
                <th className="px-4 py-3 text-left">Player</th>
                <th className="px-3 py-3 text-center">G</th>
                <th className="px-3 py-3 text-center">COMP</th>
                <th className="px-3 py-3 text-center">ATT</th>
                <th className="px-3 py-3 text-center">PCT</th>
                <th className="px-3 py-3 text-center">PASS YDS</th>
                <th className="px-3 py-3 text-center">PASS TD</th>
                <th className="px-3 py-3 text-center">INT</th>
                <th className="px-3 py-3 text-center">RUSH YDS</th>
                <th className="px-3 py-3 text-center">RUSH TD</th>
                <th className="px-3 py-3 text-center">TOTAL TD</th>
                <th className="px-3 py-3 text-center">EFFIC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {seasonTotals.map((qb, index) => (
                <tr 
                  key={qb.player_id} 
                  className={`hover:bg-slate-50 ${qb.injury_status ? 'opacity-60' : ''}`}
                  data-testid={`season-row-${qb.player_id}`}
                >
                  <td className="px-2 py-3 text-center font-bold text-slate-500">{index + 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {qb.photo_url ? (
                        <img src={qb.photo_url} alt={qb.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: qb.color || '#333' }}>
                          <span className="text-white font-bold text-sm">{qb.name?.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-[#001f3f] flex items-center gap-2">
                          {qb.name}
                          {qb.injury_status && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-1 rounded">
                              {qb.injury_status === 'OUT_TEMPORARILY' ? 'OUT' : 'SEASON'}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{qb.school}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">{qb.games_played || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.completions || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.attempts || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.completion_pct ? `${qb.completion_pct}%` : '—'}</td>
                  <td className="px-3 py-3 text-center font-bold text-blue-600">{(qb.passing_yards || 0).toLocaleString()}</td>
                  <td className="px-3 py-3 text-center font-bold text-green-600">{qb.passing_tds || 0}</td>
                  <td className="px-3 py-3 text-center text-red-500">{qb.interceptions || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.rushing_yards || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.rushing_tds || 0}</td>
                  <td className="px-3 py-3 text-center font-bold bg-[#DC2626]/10 text-[#DC2626]">{qb.total_tds || 0}</td>
                  <td className="px-3 py-3 text-center">{qb.passing_efficiency?.toFixed(1) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-sm text-slate-500 mt-4 text-center">
        Season totals reflect cumulative statistics from SWAC official records.
      </p>
    </div>
  );
}

// ==========================================
// HEAD-TO-HEAD TAB
// ==========================================
function HeadToHeadTab({ roster, seasonTotals, compareQB1, compareQB2, setCompareQB1, setCompareQB2 }) {
  const activeQBs = roster.filter(qb => qb.active_in_rankings);
  const totalsMap = Object.fromEntries(seasonTotals.map(t => [t.player_id, t]));

  const qb1Stats = totalsMap[compareQB1];
  const qb2Stats = totalsMap[compareQB2];

  const getStatComparison = (stat1, stat2, higherBetter = true) => {
    if (stat1 === stat2) return ['text-slate-600', 'text-slate-600'];
    if (higherBetter) {
      return stat1 > stat2 ? ['text-green-600 font-bold', 'text-red-500'] : ['text-red-500', 'text-green-600 font-bold'];
    }
    return stat1 < stat2 ? ['text-green-600 font-bold', 'text-red-500'] : ['text-red-500', 'text-green-600 font-bold'];
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-[#001f3f] mb-6 flex items-center gap-2">
        <Users className="h-6 w-6" />
        Head-to-Head Comparison
      </h2>

      {/* QB Selectors */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <select
            value={compareQB1}
            onChange={(e) => setCompareQB1(e.target.value)}
            className="w-full md:w-64 border-2 border-slate-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none"
            data-testid="compare-qb1"
          >
            <option value="">Select First QB</option>
            {activeQBs.map(qb => (
              <option key={qb.player_id} value={qb.player_id}>
                {qb.name} ({qb.school})
              </option>
            ))}
          </select>

          <div className="bg-[#DC2626] text-white px-4 py-2 rounded-full font-bold">VS</div>

          <select
            value={compareQB2}
            onChange={(e) => setCompareQB2(e.target.value)}
            className="w-full md:w-64 border-2 border-slate-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none"
            data-testid="compare-qb2"
          >
            <option value="">Select Second QB</option>
            {activeQBs.map(qb => (
              <option key={qb.player_id} value={qb.player_id}>
                {qb.name} ({qb.school})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comparison Results */}
      {qb1Stats && qb2Stats && (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="comparison-results">
          {/* Headers */}
          <div className="grid grid-cols-3 bg-[#001f3f] text-white py-4">
            <div className="text-center">
              <div className="flex flex-col items-center">
                {qb1Stats.photo_url ? (
                  <img src={qb1Stats.photo_url} alt={qb1Stats.name} className="w-16 h-16 rounded-full object-cover mb-2" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-600 mb-2" />
                )}
                <span className="font-bold">{qb1Stats.name}</span>
                <span className="text-sm text-slate-300">{qb1Stats.school}</span>
              </div>
            </div>
            <div className="text-center flex items-center justify-center">
              <span className="text-slate-400">STAT</span>
            </div>
            <div className="text-center">
              <div className="flex flex-col items-center">
                {qb2Stats.photo_url ? (
                  <img src={qb2Stats.photo_url} alt={qb2Stats.name} className="w-16 h-16 rounded-full object-cover mb-2" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-600 mb-2" />
                )}
                <span className="font-bold">{qb2Stats.name}</span>
                <span className="text-sm text-slate-300">{qb2Stats.school}</span>
              </div>
            </div>
          </div>

          {/* Stats Rows */}
          {[
            { label: 'Games Played', key: 'games_played' },
            { label: 'Pass Yards', key: 'passing_yards' },
            { label: 'Pass TDs', key: 'passing_tds' },
            { label: 'Interceptions', key: 'interceptions', higherBetter: false },
            { label: 'Comp %', key: 'completion_pct' },
            { label: 'Rush Yards', key: 'rushing_yards' },
            { label: 'Rush TDs', key: 'rushing_tds' },
            { label: 'Total TDs', key: 'total_tds' },
            { label: 'Efficiency', key: 'passing_efficiency' },
          ].map(({ label, key, higherBetter = true }) => {
            const val1 = qb1Stats[key] || 0;
            const val2 = qb2Stats[key] || 0;
            const [class1, class2] = getStatComparison(val1, val2, higherBetter);

            return (
              <div key={key} className="grid grid-cols-3 py-3 border-b border-slate-100 hover:bg-slate-50">
                <div className={`text-center text-lg ${class1}`}>
                  {typeof val1 === 'number' && val1 % 1 !== 0 ? val1.toFixed(1) : val1}
                  {key === 'completion_pct' && '%'}
                </div>
                <div className="text-center text-sm text-slate-500 font-medium">{label}</div>
                <div className={`text-center text-lg ${class2}`}>
                  {typeof val2 === 'number' && val2 % 1 !== 0 ? val2.toFixed(1) : val2}
                  {key === 'completion_pct' && '%'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(!compareQB1 || !compareQB2) && (
        <div className="text-center py-12 text-slate-500">
          Select two quarterbacks above to compare their 2026 season statistics.
        </div>
      )}
    </div>
  );
}

// ==========================================
// HELPER COMPONENTS
// ==========================================
function LeaderCard({ title, qb, stat, unit }) {
  if (!qb) return null;

  return (
    <div className="bg-white rounded-xl shadow p-4 text-center" data-testid={`leader-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{title}</div>
      {qb.photo_url ? (
        <img src={qb.photo_url} alt={qb.name} className="w-12 h-12 rounded-full object-cover mx-auto mb-2" />
      ) : (
        <div className="w-12 h-12 rounded-full mx-auto mb-2" style={{ backgroundColor: qb.color || '#333' }}>
          <span className="text-white font-bold text-lg leading-[48px]">{qb.name?.charAt(0)}</span>
        </div>
      )}
      <div className="font-bold text-[#001f3f] text-sm">{qb.name}</div>
      <div className="text-2xl font-bold text-[#DC2626] mt-1">
        {stat}{unit === '%' ? '%' : ` ${unit}`}
      </div>
    </div>
  );
}
